/**
 * Pure helper for OrderEarmarksSection's "Partia" (pin-batch) Select — kept
 * in its own module, not inline in the component, so it can be unit-tested
 * directly (see OrderEarmarksSection.test.tsx) without importing the whole
 * component and its transitive dependency on services/api -> axios, which
 * this project's jest/CRA config does not transform (ESM import syntax
 * error). Mirrors OrderTransportLinkModal's exported
 * getAvailableTransportsForOrderLink for the same reason.
 */

export interface BatchRow {
  id: string;
  batchNumber: string;
  purchaseDate: string | null;
  quantityRemaining: number;
  /**
   * quantityRemaining minus this batch's own ACTIVE earmark reservations
   * (from ANY order) — the actual quantity free to pin. quantityRemaining
   * alone over-states this whenever part of the batch is already reserved,
   * which used to show e.g. "Wolna dostępność: 60" while 30 units were
   * pinned; the backend earmark-creation check already enforced the correct
   * (lower) number, only the displayed figure here was wrong.
   */
  freelyAvailable: number;
  status: string;
  unitCostPLN?: number | null;
}

/**
 * Pin-batch Select options for the "Partia" dropdown.
 *
 * A batch with nothing freely available STAYS in the list, disabled, with a
 * reason — never filtered out — so the operator sees it exists and is taken
 * rather than wondering where it went.
 */
export function buildBatchSelectOptions(
  batches: BatchRow[],
  t: (key: string) => string,
): Array<{ value: string; disabled: boolean; label: string }> {
  return batches.map((b) => ({
    value: b.id,
    disabled: b.freelyAvailable <= 0,
    label: b.freelyAvailable > 0
      ? `${b.batchNumber} — ${t('orders:orderDetail.earmarkBatchAvailable')}: ${b.freelyAvailable}`
      : `${b.batchNumber} — ${t('orders:orderDetail.earmarkBatchFullyReserved')}`,
  }));
}
